// iframe Configuration and URL Parameter Parser
export interface IframeConfig {
  viewMode: 'full' | 'map-only' | 'statistics-only'
  colors: {
    forest?: string
    seed?: string
    background?: string
    mainBackground?: string
  }
  hideHeader?: boolean
  hideFooter?: boolean
  hideNavigation?: boolean
  initialRoute?: string
}

// Default configuration
const defaultConfig: IframeConfig = {
  viewMode: 'full',
  colors: {},
  hideHeader: false,
  hideFooter: false,
  hideNavigation: false,
  initialRoute: '/'
}

// Parse URL parameters for iframe customization
export function parseIframeConfig(): IframeConfig {
  const urlParams = new URLSearchParams(window.location.search)
  const config: IframeConfig = { ...defaultConfig }

  // Debug logging
  if (urlParams.get('debug') === 'true') {
    console.log('🔧 Iframe Config Debug Mode Enabled')
    console.log('📋 URL Parameters:', Object.fromEntries(urlParams.entries()))
  }

  // Parse view mode
  const viewMode = urlParams.get('view')
  if (viewMode === 'map' || viewMode === 'map-only') {
    config.viewMode = 'map-only'
    config.hideHeader = true
    config.hideFooter = true
    config.hideNavigation = true
    config.initialRoute = '/map'
  } else if (viewMode === 'stats' || viewMode === 'statistics-only') {
    config.viewMode = 'statistics-only'
    config.hideHeader = true
    config.hideFooter = true
    config.hideNavigation = true
    config.initialRoute = '/statistics'
  }

  // Parse individual hide flags
  if (urlParams.get('hideHeader') === 'true') {
    config.hideHeader = true
  }
  if (urlParams.get('hideFooter') === 'true') {
    config.hideFooter = true
  }
  if (urlParams.get('hideNavigation') === 'true') {
    config.hideNavigation = true
  }

  // Parse initial route
  const route = urlParams.get('route')
  if (route && ['/', '/map', '/statistics'].includes(route)) {
    config.initialRoute = route
  }

  // Parse color overrides
  const colors: IframeConfig['colors'] = {}
  
  const forestColor = urlParams.get('forestColor')
  if (forestColor && isValidColor(forestColor)) {
    colors.forest = forestColor
  }
  
  const seedColor = urlParams.get('seedColor')
  if (seedColor && isValidColor(seedColor)) {
    colors.seed = seedColor
  }
  
  const backgroundColor = urlParams.get('backgroundColor')
  if (backgroundColor && isValidColor(backgroundColor)) {
    colors.background = backgroundColor
  }
  
  const mainBackgroundColor = urlParams.get('mainBackgroundColor')
  if (mainBackgroundColor && isValidColor(mainBackgroundColor)) {
    colors.mainBackground = mainBackgroundColor
  }

  // Parse color theme (preset color combinations)
  const theme = urlParams.get('theme')
  if (theme) {
    const themeColors = getThemeColors(theme)
    if (themeColors) {
      Object.assign(colors, themeColors)
    }
  }

  config.colors = colors

  // Debug logging
  if (urlParams.get('debug') === 'true') {
    console.log('🎨 Final Config:', config)
    console.log('🎨 Custom Colors:', colors)
  }

  return config
}

// Validate color format (hex, rgb, rgba, or named colors)
function isValidColor(color: string): boolean {
  // Hex colors
  if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
    return true
  }
  
  // RGB/RGBA colors
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+)?\s*\)$/.test(color)) {
    return true
  }
  
  // HSL/HSLA colors
  if (/^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+)?\s*\)$/.test(color)) {
    return true
  }
  
  // Named colors (basic set)
  const namedColors = [
    'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink', 'brown',
    'black', 'white', 'gray', 'grey', 'transparent'
  ]
  return namedColors.includes(color.toLowerCase())
}

// Get preset theme colors
function getThemeColors(theme: string): IframeConfig['colors'] | null {
  const themes: Record<string, IframeConfig['colors']> = {
    'nature': {
      forest: '#2d5016',
      seed: '#f4f1e8',
      background: '#8fbc8f',
      mainBackground: '#f5f5dc'
    },
    'ocean': {
      forest: '#1e3a8a',
      seed: '#e0f2fe',
      background: '#0ea5e9',
      mainBackground: '#f0f9ff'
    },
    'earth': {
      forest: '#92400e',
      seed: '#fef3c7',
      background: '#d97706',
      mainBackground: '#fffbeb'
    },
    'forest': {
      forest: '#1f4529',
      seed: '#f2ecd2',
      background: '#719f6f',
      mainBackground: '#f2ecd2'
    },
    'minimal': {
      forest: '#374151',
      seed: '#f9fafb',
      background: '#6b7280',
      mainBackground: '#ffffff'
    }
  }
  
  return themes[theme] || null
}

// Check if running in iframe
export function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

// Generate iframe URL with parameters
export function generateIframeUrl(baseUrl: string, config: Partial<IframeConfig>): string {
  const params = new URLSearchParams()
  
  if (config.viewMode) {
    if (config.viewMode === 'map-only') {
      params.set('view', 'map')
    } else if (config.viewMode === 'statistics-only') {
      params.set('view', 'stats')
    }
  }
  
  if (config.hideHeader) {
    params.set('hideHeader', 'true')
  }
  
  if (config.hideFooter) {
    params.set('hideFooter', 'true')
  }
  
  if (config.hideNavigation) {
    params.set('hideNavigation', 'true')
  }
  
  if (config.initialRoute) {
    params.set('route', config.initialRoute)
  }
  
  if (config.colors) {
    if (config.colors.forest) {
      params.set('forestColor', config.colors.forest)
    }
    if (config.colors.seed) {
      params.set('seedColor', config.colors.seed)
    }
    if (config.colors.background) {
      params.set('backgroundColor', config.colors.background)
    }
    if (config.colors.mainBackground) {
      params.set('mainBackgroundColor', config.colors.mainBackground)
    }
  }
  
  const queryString = params.toString()
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}
